import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { TableContext } from '../../../contexts/TableContext';
import { HeaderIcons } from '../../../contexts/TablesContext/DashboardsTableProvider';
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";
import AddCustomField from '../../atoms/AddCustomField';
import { FallbackLoading } from '../../templates/loader';

const useStyles = makeStyles((theme) => ({
    fields: {
        height: '100%',
        padding: '8px 12px 60px 8px',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    card: {
        position: 'relative',
        minWidth: 360,
        width: '100%',
        height: 'fit-content',
        minHeight: 80,
        padding: '12px 16px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['400']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2)',
        transition: 'all 300ms ease-in-out',
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '24px !important',
            height: '24px !important',
        }
    },
    icon: {
        padding: 0,
        "& > svg": {
            width: '28px !important',
            height: '28px !important'
        },
    },
    field: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    customBtn: {
        width: '22ch',
        height: 32,
        borderRadius: 16,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontSize: '0.85rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    editBtn: {
        padding: 'unset',
        minWidth: 'fit-content',
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '0.85rem',
        color: theme.palette.primary.main,
        textTransform: 'none',
        textDecoration: 'none',
        "&:hover": {
            background: 'transparent',
        },
    },
}));

export const Captions: { [key: string]: string } = {
    'Recent Activity': 'Shows new deal or update to existing deal',
    'Status': 'User can specify status in forwared emails',
    'Stage': 'Set by AI using email context',
    'Sectors': 'Set by AI using email context',
    'Key People': 'Founders and executives of the company',
    'Deck/Teaser': 'Captured from emails as PDFs or links',
    'Owner': 'Person that forwarded the deal',
    'Source': 'Person that originated the deal',
    'LinkedIn Posts': 'Captured from LinkedIn',
    'Communications': 'Emails exchanged with company',
    'Commentary': 'User can add comments in forwarded emails',
    'Description': 'AI-generated from context',
    'Location': 'Set by third-party data sources',
    'Sync Status': 'Auto-sync or manually sync to CRM',
    'Emails': 'Source emails forwarded for the deal',
};

const ManageFields: React.FC<{
    onOpenModal?: (field?: string) => void,
}> = ({ onOpenModal }) => {
    const classes = useStyles();
    const { tagGroups, customFields } = useContext(GroupSettingsContext);
    const { headers, setHeaders, visibleHeaders, setVisibleHeaders } = useContext(TableContext);
    const [hoverAt, setHoverAt] = useState<boolean[]>([]);

    const editableFields = useMemo(() => [
        'Status', 'Stage', ...tagGroups.map(({ title }) => title), ...customFields.map(({ name }) => name), 'Key People'
    ], [tagGroups, customFields]);

    const showColumn = useCallback((column: string) => !['Selection', 'Name', 'Updated', 'Added'].includes(column) && !column.startsWith('[') && !column.endsWith(']'), []);

    const handleCheck = useCallback((e: any, index: number) => {
        e.stopPropagation();
        e.preventDefault();
        setVisibleHeaders(prev => [...prev ?? []].map((state, i) => index === i ? !state : state));
    }, [setVisibleHeaders]);

    const dragAndDropStatus = useCallback((result: DropResult) => {
        const { source, destination, type } = result;

        if (!destination)
            return;

        switch (type) {
            case 'group': (() => {
                const sourceIdx = source.droppableId === 'manage-fields' ? source.index : -1;
                const destIdx = destination.droppableId === 'manage-fields' ? destination.index : -1;

                if (sourceIdx !== destIdx && destIdx >= 0) {
                    const newOrder = [...headers ?? []];
                    const nextVisible = [...visibleHeaders ?? []];
                    const movedHeader = newOrder.splice(sourceIdx, 1)[0];
                    const movedVisible = nextVisible.splice(sourceIdx, 1)[0];

                    newOrder.splice(destIdx, 0, movedHeader);
                    nextVisible.splice(destIdx, 0, movedVisible);
                    setHeaders(newOrder);
                    setVisibleHeaders(nextVisible);
                }
            })(); break;
        default:
            break;
        }
    // eslint-disable-next-line
    }, [headers, visibleHeaders]);

    return (<>
        <Stack direction="column" spacing={2} width="50%" height="100%" p={2}>
            {/* sx={{ border: '3px dashed #00bcd4', borderRadius: 6, }}> */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" >
                <Typography fontFamily="Inter" fontSize="1.1rem" color="#666666" fontWeight={700} pl={1}>
                    {'Customize fields: '}
                </Typography>
                <AddCustomField
                    onNewTag={() => onOpenModal?.('[[new-tag]]')}
                    onNewField={() => onOpenModal?.('[[new-field]]')} />
            </Stack>
            <DragDropContext onDragEnd={dragAndDropStatus}>
                <Droppable droppableId="manage-fields" type="group" direction="vertical">
                    {(provided) => (
                        <Stack direction="column" className={classes.fields} spacing={2} alignItems="flex-start"
                            {...provided.droppableProps} ref={provided.innerRef}>
                            {!headers?.length ? (<FallbackLoading />) : headers?.map((field, i) => showColumn(field) && (
                                <Draggable draggableId={'manage-field:' + field} index={i} key={'manage-fields-155-' + field}>
                                    {(provided) => (
                                        <Stack className={classes.card} alignItems="flex-start" justifyContent="center"
                                            {...provided.draggableProps} ref={provided.innerRef}
                                            style={{ ...provided.draggableProps.style }}
                                            onMouseEnter={() => setHoverAt(prev => { prev[i] = true; return [...prev];})}
                                            onMouseLeave={() => setHoverAt([])}
                                            key={'manage-fields-162-' + i}>
                                            <Stack spacing="4px" width="100%"
                                                onClick={(e) => handleCheck(e, i)}
                                                sx={{ cursor: 'pointer' }} >
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                                                        {(visibleHeaders?.[i] || hoverAt[i]) ? (
                                                            <Checkbox edge="end"
                                                                className={classes.checkBox}
                                                                checked={visibleHeaders?.[i]}
                                                                disableRipple />
                                                        ) : (
                                                            <IconButton className={classes.icon} disabled>
                                                                {!!HeaderIcons[field] && HeaderIcons[field]}
                                                                {!!tagGroups.find(({title}) => title === field && field !== 'Sectors') && HeaderIcons['Sectors']}
                                                                {!HeaderIcons[field] && !tagGroups.find(({title}) => title === field) && (<Box width={24} height={24} />)}
                                                            </IconButton>
                                                        )}
                                                        <Typography className={classes.field}>{field === 'Key People' ? 'Team': field}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                        {editableFields.includes(field) && (
                                                            <Button variant="text"
                                                                className={classes.editBtn}
                                                                onClick={(e) => {e.stopPropagation(); e.preventDefault(); onOpenModal?.(field)}}>
                                                                {'Edit'}
                                                            </Button>
                                                        )}
                                                        <Box {...provided.dragHandleProps} height={24}>
                                                            <DragIndicatorIcon fontSize="medium" sx={{ "&.MuiSvgIcon-root": { fill: '#666666' } }} />
                                                        </Box>
                                                    </Stack>
                                                </Stack>
                                                <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" pl="36px">
                                                    <Typography fontFamily="Inter" fontSize="0.85rem" color={theme.colors.neutral['500']} fontWeight={400}>
                                                        {Captions[field] || 'Assigned using AI'}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Stack>
                    )}
                </Droppable>
            </DragDropContext>
        </Stack>
    </>);
}

export default ManageFields;
